import type { FeedItem } from '@knocklabs/client';
import type { GenericData } from '@knocklabs/types/src/common';

import notifications from './notification-types';
import type {
  GetItem,
  NotificationLookupData,
} from './notification-types/types';

export type NotificationItem = {
  url: null | string;
  /** The type/event label, e.g. "New Risk". `null` for sentence-style notifications. */
  type: null | string;
  /** The entity name (or the whole sentence, for sentence-style notifications). */
  name: string;
  feedItem: FeedItem<GenericData>;
  id: null | string;
};

export type KnockRenderedContent = {
  type: null | string;
  name: string;
  url: null | string;
};

/** Which source rendered a feed item's in-app content. */
export type RenderPath = 'knock' | 'fallback' | 'dropped';

/**
 * Telemetry-only view of how a feed item resolved, derived from the SAME decision the
 * renderer makes (`resolveRenderPath`) so the metric can never drift from real behaviour.
 * Carries no message text or entity data — only the bounded dimensions the cutover
 * dashboard needs (RSP-6347). `insertedAt` is raw; the age bucket is computed at emit time.
 */
export type RenderSignal = {
  id: string;
  renderPath: RenderPath;
  workflowKey: string | null;
  knockHasLink: boolean;
  handlerExists: boolean;
  insertedAt: string | null;
};

type Block = FeedItem<GenericData>['blocks'][number];

const isTextBlock = (
  block: Block
): block is Extract<Block, { type: 'markdown' | 'text' }> =>
  block.type === 'markdown' || block.type === 'text';

const isButtonSetBlock = (
  block: Block
): block is Extract<Block, { type: 'button_set' }> =>
  block.type === 'button_set';

/** Block-level element boundaries — each becomes one rendered segment. */
const BLOCK_BOUNDARY = /<\/(?:p|h[1-6]|div|li|blockquote)>/i;

/**
 * Strip a chunk of Knock's Liquid-evaluated `rendered` HTML to plain text.
 *
 * A feed block carries two fields: `content` is the RAW template source (still holding the
 * un-evaluated `{{ … }}`/`{% … %}` Liquid), and `rendered` is the evaluated output as HTML.
 * We must read `rendered` to get the actual content — but never inject that HTML into the
 * DOM (ENG-184: an entity title could smuggle in markup). Stripping to plain text here, and
 * the cell rendering the result as an escaped React text node, keeps it injection-safe.
 */
const htmlToPlainText = (html: string): string =>
  html
    .replace(/<[^>]+>/g, ' ') // tags -> space (don't word-join across inline tags)
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#0?39;/g, "'")
    .replace(/&#x27;/gi, "'")
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&') // ampersand last, so the other entities decode first
    .replace(/\s+/g, ' ')
    .trim();

/**
 * Split the first text block's rendered HTML into ordered, plain-text segments — one per
 * block-level element. The in-app template emits the type label and the entity name as
 * separate paragraphs, so the segments come back in order and the renderer reads them
 * positionally rather than parsing a delimiter out of one string.
 */
const renderedSegments = (html: string | null | undefined): string[] => {
  if (!html) {
    return [];
  }

  return html
    .split(BLOCK_BOUNDARY)
    .map(htmlToPlainText)
    .filter((segment) => segment.length > 0);
};

/**
 * Read Knock-rendered in-app content off a feed item.
 *
 * The template renders the type label and the entity name as two block-level paragraphs;
 * we read them positionally from the Liquid-evaluated `rendered` output (NOT the raw
 * `content`, which still holds un-evaluated Liquid). Segment 0 is the type, the rest is the
 * name. A single segment is a sentence-style notification (no type line). The deep link is
 * the first button_set button's `action`.
 *
 * Returns `null` when Knock rendered no usable text (e.g. a workflow whose in-app template
 * is still empty), so callers fall back to the legacy per-workflow handler.
 */
export const extractKnockContent = (
  feedItem: FeedItem<GenericData>
): KnockRenderedContent | null => {
  const blocks = feedItem.blocks ?? [];

  const segments = renderedSegments(blocks.find(isTextBlock)?.rendered);
  if (segments.length === 0) {
    return null;
  }

  const action = blocks.find(isButtonSetBlock)?.buttons[0]?.action;
  const url = action || null;

  if (segments.length === 1) {
    return { type: null, name: segments[0], url };
  }

  const [type, ...rest] = segments;

  return { type, name: rest.join(' '), url };
};

/**
 * Split a legacy fallback handler's single "<Type>: <name>" string into the same
 * `{ type, name }` shape the Knock path produces, so the cell renders both paths
 * uniformly. Confined to the fallback branch; removed with it in Stage 6 (RSP-6235).
 */
const splitMessage = (
  message: string
): { type: null | string; name: string } => {
  const separator = message.indexOf(': ');
  if (separator === -1) {
    return { type: null, name: message };
  }

  return {
    type: message.slice(0, separator),
    name: message.slice(separator + 2),
  };
};

/**
 * The single render decision, shared by `buildNotificationItem` (what to show) and
 * `buildRenderSignal` (what to report). Centralising it here is what guarantees the cutover
 * telemetry (RSP-6347) measures exactly what the user sees.
 *
 * Knock content is used when it carries content AND either a deep link was rendered or
 * there is no legacy handler for this workflow (in which case Knock content is strictly
 * better than the nothing rendered today). A handler-backed item whose Knock content lacks
 * a link falls back to the handler so navigation is preserved. `dropped` when neither path
 * can render — preserving the prior behaviour of dropping items with no handler.
 */
export const resolveRenderPath = (
  knock: KnockRenderedContent | null,
  handler: GetItem | undefined
): RenderPath => {
  if (knock && (knock.url !== null || !handler)) {
    return 'knock';
  }

  if (handler) {
    return 'fallback';
  }

  return 'dropped';
};

const classifyFeedItem = (
  feedItem: FeedItem<GenericData>
): {
  handler: GetItem | undefined;
  knock: KnockRenderedContent | null;
  path: RenderPath;
} => {
  // `source` is typed non-optional but Knock's real-time socket can deliver partial
  // items; guard so one malformed message drops out rather than throwing in the map.
  const handler = feedItem.source?.key
    ? notifications[feedItem.source.key]
    : undefined;
  const knock = extractKnockContent(feedItem);

  return { handler, knock, path: resolveRenderPath(knock, handler) };
};

/**
 * Build the rendered `NotificationItem` for a feed item, preferring Knock-templated
 * content over the legacy app-side handler (see `resolveRenderPath`). Returns `null` when
 * neither path can render.
 */
export const buildNotificationItem = (
  feedItem: FeedItem<GenericData>,
  lookupData: NotificationLookupData
): NotificationItem | null => {
  const { handler, knock, path } = classifyFeedItem(feedItem);

  if (path === 'knock' && knock) {
    // The friendly id (e.g. `R-187`) rides on the payload as `data.objectFriendlyId`
    // — formatted once server-side (`getFriendlyId`) — so the cell renders it without
    // re-parsing it out of the message text. Absent for flows that don't set it.
    const friendlyId = feedItem.data?.objectFriendlyId;

    return {
      type: knock.type,
      name: knock.name,
      url: knock.url,
      id: typeof friendlyId === 'string' ? friendlyId : null,
      feedItem,
    };
  }

  if (path === 'fallback' && handler) {
    const details = handler(feedItem, lookupData);
    const { type, name } = splitMessage(details.message ?? '');

    return { type, name, url: details.url, id: details.id, feedItem };
  }

  return null;
};

/**
 * Derive the cutover-telemetry signal for a feed item — the same classification the
 * renderer uses, minus any message/entity content. Consumed by
 * `useNotificationRenderTelemetry` to count Knock-rendered vs app-hydration (fallback)
 * renders while the migration runs (RSP-6347); removed alongside the fallback machinery in
 * Stage 6 (RSP-6235).
 */
export const buildRenderSignal = (
  feedItem: FeedItem<GenericData>
): RenderSignal => {
  const { handler, knock, path } = classifyFeedItem(feedItem);

  return {
    id: feedItem.id,
    renderPath: path,
    workflowKey: feedItem.source?.key ?? null,
    knockHasLink: knock?.url != null,
    handlerExists: handler !== undefined,
    insertedAt: feedItem.inserted_at ?? null,
  };
};
